'use client';

import React from 'react';
import { z } from 'zod';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Col, Row, UploadFile, message } from 'antd';
import FormField from '@/components/FormField/FormField';
import Button from '@/components/atoms/Button';
import { useTranslation } from 'react-i18next';
import { useUploadProductCertificateByAdminMutation } from '../../../../../../store/services/products';
import type { AllowedFileTypes } from '@/components/FormField/interfaces';
import { useDispatch } from 'react-redux';
import { closeModal } from '@/store/slices/Modals';

interface CustomFile extends UploadFile {
  blobSize?: number;
  blobName?: string;
  blobFileUrl?: string;
  id?: number | string;
}

type Props = {
  initData?: {
    uid: string;
    name: string;
    url: string;
    certificateName: string;
    size?: number;
    type?: string;
  } | null;
  onSuccess: (certificate: {
    uid: string;
    name: string;
    url: string;
    certificateName: string;
    size?: number;
    type?: string;
  }, oldUid?: string) => void;
};

const AddCertificateModal = ({ onSuccess, initData }: Props) => {
  const { t: t } = useTranslation();
  const { t: tValidations } = useTranslation();
  const { t: tComponents } = useTranslation();

  const dispatch = useDispatch();

  const regex =
    /^[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF\u0020-\u007E\u00A0-\u00FF\u0100-\u024F0-9!@#$%^&*()_+={}[\]:;"'<>,.?/\\|\s-]*$/;

  const validationSchema = z.object({
    certificateName: z
      .string({ required_error: tValidations('requiredField') })
      .trim()
      .min(3, { message: tValidations('minLength', { value: 3 }) })
      .max(20, { message: tValidations('maxLength', { value: 20 }) })
      .regex(regex, { message: tComponents('arabic_english_char_numbers') }),
    file: z
      .array(z.custom<CustomFile>())
      .min(1, { message: tValidations('requiredField') })
      .max(1, { message: tValidations('maxItems', { value: 1 }) }),
  });

  type FieldsTypes = z.infer<typeof validationSchema>;

  const methods = useForm<FieldsTypes>({
    resolver: zodResolver(validationSchema),
    mode: 'all',
    defaultValues: {
      certificateName: initData?.certificateName || '',
      file: initData ? [{
        uid: initData.uid,
        name: initData.name,
        url: initData.url,
        status: 'done',
        type: initData.type || 'application/pdf',
      }] as CustomFile[] : [],
    },
  });

  const [uploadCertificate, { isLoading: loading }] = useUploadProductCertificateByAdminMutation();

  const addCertificate = async (dataForm: FieldsTypes) => {
    try {
      const fileToUpload = dataForm.file[0];

      if (!fileToUpload) {
        throw new Error('No file selected');
      }

      if (fileToUpload.originFileObj) {
        // New file uploaded
        const formData = new FormData();
        formData.append('files', fileToUpload.originFileObj);

        const response = await uploadCertificate(formData).unwrap();
        const item = Array.isArray(response?.payload) ? response?.payload[0] : response?.payload;

        if (item?.id) {
          onSuccess({
            uid: item.id as string,
            name: item.blobName || item.name || fileToUpload.name,
            url: item.blobFileUrl,
            certificateName: dataForm.certificateName,
            size: item.blobSize || fileToUpload.size,
            type: fileToUpload.type,
          }, initData?.uid);
        } else {
          throw new Error('Invalid response');
        }
      } else {
        // File unchanged, only name might have changed
        onSuccess({
          uid: fileToUpload.uid,
          name: fileToUpload.name,
          url: fileToUpload.url || '',
          certificateName: dataForm.certificateName,
          size: fileToUpload.size,
          type: fileToUpload.type,
        }, initData?.uid);
      }

      methods.reset();
      dispatch(closeModal({ id: 'CERTIFICATE_MODAL' }));
    } catch (error) {
      console.error("🚀 ~ addCertificate ~ error:", error)
      message.error(tComponents('somthingWentWrong'));
    }
  };

  const imgAllowedTypes: AllowedFileTypes[] = [
    'image/jpeg',
    'image/png',
    'image/webp',
    'application/pdf',
  ] as AllowedFileTypes[];

  return (
    <FormProvider {...methods}>
      <div data-testid="add-certificate-form">
        <Row gutter={[24, 24]}>
          <Col span={24}>
            <FormField
              id="certificateName"
              name="certificateName"
              type="text"
              required
              label={t('certificateNameLabel')}
              placeholder={t('certificateNamePlaceholder')}
              data-testid="add-certificate-name-input"
            />
          </Col>
          <Col span={24}>
            <FormField
              type="file"
              name="file"
              id="certificate-file-uploader"
              label={t('certificateFileLabel')}
              maxCount={1}
              allowedTypes={imgAllowedTypes}
              uploadHints={[
                tComponents('components.acceptedFileTypesHint', {
                  types: '.jpg, .png, .pdf',
                }),
                tComponents('components.maxFilesHint', { count: 1, size: '5MB' }),
              ]}
              maxFileSize={5}
              multiple={false}
              data-testid="add-certificate-file-input"
            />
          </Col>
        </Row>
        <div
          className="flex flex-row-reverse mt-10"
          data-testid="add-certificate-form-actions"
        >
          <Button
            type="button"
            onClick={methods.handleSubmit(addCertificate)}
            disabled={loading}
            data-testid="add-certificate-modal-submit"
          >
            {tComponents('components.add')}
          </Button>
        </div>
      </div>
    </FormProvider>
  );
};

export default AddCertificateModal;
